import {
  Flex,
  Grid,
  Text,
  Tooltip,
  useColorModeValue,
  Portal,
  Box,
  Input,
  useToast,
  Button,
  Image,
  Stack,
  Spacer,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { RepeatIcon, CalendarIcon } from '@chakra-ui/icons';
import { CloseIcon } from '@chakra-ui/icons';
import { HSeparator } from 'components/Separator/Separator';
import CountDown from 'components/CountDown/CountDown';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ReactSVG } from 'react-svg';
import './project.css';
import { selectedShowAtom, showsScheduleBookingDetailAtom, showsScheduleAtom } from 'state/show';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useBookingActions } from 'hooks/useBookingActions';
import { checkForbidden } from 'helpers/checkForbidden';
import moment from 'moment';
import { useHistory, Prompt } from 'react-router-dom';
import { showTextFloor } from 'variables/columnsData';
import { NumericFormat } from 'react-number-format';
import { debounce } from 'lodash';
import EventCalendar from 'components/Calendars/EventCalendar';
import { getFirstAndLastDate } from 'helpers/date';
import {
  openCustomerDisplayWindow,
  useCustomerDisplayState,
} from 'helpers/customerDisplayChannel';
import { applySeatStyling, colorBackGround } from 'helpers/seatStyling';
import CustomerDisplayPreview from './CustomerDisplayPreview';

const SmallInput = React.forwardRef((props, ref) => (
  <Input
    ref={ref}
    {...props}
    sx={{
      height: '32px !important',
      minHeight: '32px !important',
      width: '60px !important',
      fontSize: '14px !important',
    }}
  />
));

function BookingInformation() {
  const [selectedSeat, setSelectedSeat] = useState([]);
  const [lockByAdmin, setLockByAdmin] = useState('');
  const [seatWaiting, setSeatWaiting] = useState('');
  const [seatBooked, setSeatBooked] = useState('');
  const [desktopMap, setDesktopMap] = useState('');
  const [infoSelectedSeat, setInfoSelectedSeat] = useState({});
  const selectedShow = useRecoilValue(selectedShowAtom);
  const listAssignSeatingPlan = useRecoilValue(showsScheduleBookingDetailAtom);
  const showsSchedule = useRecoilValue(showsScheduleAtom);
  const setSelectedShow = useSetRecoilState(selectedShowAtom);
  const bookingActions = useBookingActions();
  const bookingRef = useRef();
  const [adult, setAdult] = useState(0);
  const [child, setChild] = useState(0);
  const [legendMap, setLegendMap] = useState();
  const [ticketPrices, setTicketPrices] = useState([]);
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const cancelRef = useRef();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const svgRef = useRef(null);
  const history = useHistory();
  const [isNavigating, setIsNavigating] = useState(false);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const leaveRef = useRef();
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
  const [yearUpdate, setYearUpdate] = useState(new Date().getFullYear());
  const [monthUpdate, setMonthUpdate] = useState(new Date().getMonth());
  const [isChangingShow, setIsChangingShow] = useState(false);
  const [calendarModalKey, setCalendarModalKey] = useState(0);
  const calendarApiCallRef = useRef(null);
  const isProgrammaticNavigation = useRef(false);
  const [pendingSeatRequests, setPendingSeatRequests] = useState(0);
  const isMountedRef = useRef(true);
  const adultRef = useRef(0);
  const childRef = useRef(0);
  const ticketUpdateSeqRef = useRef(0);
  const { isDisplayConnected, sendMessage: sendCustomerDisplayMessage } = useCustomerDisplayState();

  // Track component mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  function getBookingSummary() {
    return {
      showName: selectedShow?.booking?.show?.name,
      venueName: selectedShow?.booking?.venue?.name,
      venueAddress: selectedShow?.booking?.venue?.address,
      showDate: selectedShow?.booking?.show_date
        ? moment(selectedShow.booking.show_date).format('DD/MM/YYYY')
        : '',
      showTime: selectedShow?.booking?.show_time?.slice(0, -3),
      // Raw input strings ("02") until parsed — NumericFormat only strips the
      // leading zero from its own display on blur, never from our state.
      adult: parseInt(adult || 0),
      child: parseInt(child || 0),
      zone: infoSelectedSeat?.seats?.[0]?.zone?.name,
      floor: concatenatedStrings,
      seatNo: infoSelectedSeat?.seats
        ?.map((seat) => seat.seat_number)
        .join(', '),
      expireAt: selectedShow?.booking?.expire_at,
      seats: infoSelectedSeat?.seats || [],
      ticketPrices,
    };
  }

  // Shared by the live-mirror effect below and by handleCloseCalendarModal,
  // which needs to explicitly hand the Customer Display back to this once
  // Change Show Date closes (see the 'calendar' broadcast further down).
  function broadcastSeatingScreen() {
    if (!desktopMap) return;
    sendCustomerDisplayMessage({
      type: 'seating',
      data: {
        desktopMap,
        selectedSeat,
        lockByAdmin,
        seatWaiting,
        seatBooked,
        legendMap,
        ...getBookingSummary(),
      },
    });
  }

  // Mirror live seat selection + booking summary to the Customer Display as it changes
  useEffect(() => {
    broadcastSeatingScreen();
  }, [
    desktopMap,
    selectedSeat,
    lockByAdmin,
    seatWaiting,
    seatBooked,
    legendMap,
    ticketPrices,
    infoSelectedSeat,
    adult,
    child,
  ]);

  // Mirrors the "Change Show Date" calendar onto the Customer Display while
  // it's open, so the guest can watch the operator browse for a new
  // date/time instead of staring at a frozen seat map. Read-only on that
  // side (BookingCustomerDisplay.js wires no click/navigation handlers into
  // it) — only handleCloseCalendarModal below can hand the display back to
  // the live seating screen once it closes.
  useEffect(() => {
    if (!isCalendarModalOpen) return;
    sendCustomerDisplayMessage({
      type: 'calendar',
      data: {
        ...getBookingSummary(),
        calendarData: showsSchedule.filter((show) => {
          // Only show events from today onwards — mirrors the same filter
          // the operator's own modal applies below.
          const showDate = moment(show.start);
          const today = moment().startOf('day');
          return showDate.isSameOrAfter(today);
        }),
        initialDate: moment().month(monthUpdate).year(yearUpdate).startOf('month').format('YYYY-MM-DD'),
        yearUpdate,
        monthUpdate,
      },
    });
  }, [isCalendarModalOpen, showsSchedule, monthUpdate, yearUpdate, infoSelectedSeat, adult, child, ticketPrices]);

  function broadcastBookingConfirmation() {
    sendCustomerDisplayMessage({
      type: 'confirmation',
      data: getBookingSummary(),
    });
  }

  // Send the Customer Display back to its idle/welcome screen once the seat
  // hold expires or the booking session is cancelled, instead of leaving it
  // stuck on the last seating/confirmation state it was showing.
  function resetCustomerDisplay() {
    // Tag the reset with this session's expireAt so the display can tell it
    // apart from an unrelated booking's cancel/expiry — see the matching
    // guard in BookingCustomerDisplay.js.
    sendCustomerDisplayMessage({
      type: 'idle',
      expireAt: selectedShow?.booking?.expire_at,
    });
  }

  // Check if booking session exists on mount
  useEffect(() => {
    let timeoutId;
    let isMounted = true;

    const checkAndRestoreSession = () => {
      // If no booking session, redirect to create page
      if (!selectedShow?.booking_session_id) {
        toast({
          title: 'Session not found',
          description: 'Booking session not found. Redirecting to booking page.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
          onCloseComplete: () => {
            if (isMounted) {
              window.location.href = '/#/admin/booking/create';
            }
          },
        });
        // Also redirect after 2 seconds if user doesn't close the toast
        timeoutId = setTimeout(() => {
          if (isMounted) {
            window.location.href = '/#/admin/booking/create';
          }
        }, 2000);
      }
    };

    checkAndRestoreSession();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Update shouldBlockNavigation based on user input
  useEffect(() => {
    const hasUserInput = parseInt(adult || 0) > 0 || parseInt(child || 0) > 0 || selectedSeat.length > 0;
    setShouldBlockNavigation(hasUserInput);
  }, [adult, child, selectedSeat]);

  const fetchData = async (showToast = false, skipBookingInfo = false, isUserRefresh = false) => {
    try {
      if (isUserRefresh) {
        setIsRefreshing(true);
      }

      // Check if session is valid before making API calls
      if (!selectedShow?.booking?.show_schedule_detail_id || selectedShow?.booking?.show_schedule_detail_id === 0) {
        setShouldBlockNavigation(false);
        toast({
          title: 'Session Closed',
          description: 'Your booking session has been closed. Redirecting to booking page.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setTimeout(() => {
          if (isMountedRef.current) {
            window.location.href = '/#/admin/booking/create';
          }
        }, 2000);
        return;
      }

      await bookingActions.getListBookingDetails(
        {
          show_schedule_detail_id:
            selectedShow?.booking?.show_schedule_detail_id,
        },
        0,
        ''
      );
      if (!isMountedRef.current) return;

      const { data } = await bookingActions.getSeatingPlan(
        selectedShow?.booking?.seatingplan_id
      );
      if (!isMountedRef.current) return;

      if (data) {
        setDesktopMap(`${data.data.desktop_maps}?type=svg`);
        setLegendMap(`${data.data.legend_desktop_maps}`);
      }

      if (!skipBookingInfo) {
        const {
          data: { data: bookingData },
        } = await bookingActions.getBookingInfo();
        if (!isMountedRef.current) return;

        if (bookingData) {
          const seatsChoose =
            bookingData.seats?.map(
              (i) => `lune_seat_${i.seat_number}_${i.floor}_${i.zone?.id || i.zone_id}`
            ) || [];
          setSelectedSeat(seatsChoose);
          setInfoSelectedSeat(bookingData);
          setAdult(bookingData.adult);
          setChild(bookingData.child);
          adultRef.current = bookingData.adult;
          childRef.current = bookingData.child;
        }
      }

      const {
        data: { data: seatingLockData },
      } = await bookingActions.getSeatingLock();
      if (!isMountedRef.current) return;

      if (seatingLockData) {
        const locks = seatingLockData.map(
          (i) => `lune_seat_${i.seat_number}_${i.floor}_${i.zone_id}`
        );
        setLockByAdmin(locks);
      } else {
        setLockByAdmin([]);
      }
      const {
        data: { data: seatingWaitingData },
      } = await bookingActions.getSeatingWaitingPayment();
      if (!isMountedRef.current) return;

      if (seatingWaitingData) {
        const seatingWaiting = seatingWaitingData.map(
          (i) => `lune_seat_${i.seat_number}_${i.floor}_${i.zone_id}`
        );
        setSeatWaiting(seatingWaiting);
      } else {
        setSeatWaiting([]);
      }
      const {
        data: { data: seatingBookedData },
      } = await bookingActions.getSeatingBooked();
      if (!isMountedRef.current) return;

      if (seatingBookedData) {
        const seatingBooked = seatingBookedData.map((i) => ({
          seat: `lune_seat_${i.seat_number}_${i.floor}_${i.zone_id}`,
          status: i.status,
        }));
        setSeatBooked(seatingBooked);
      } else {
        setSeatBooked([]);
      }

      const {
        data: { data: ticketPrices },
      } = await bookingActions.getTicketPrices({
        show_id: selectedShow.booking.show.id,
        venue_id: selectedShow.booking.venue.id,
        seatingplan_id: selectedShow.booking.seatingplan_id,
      });
      if (!isMountedRef.current) return;

      setTicketPrices(ticketPrices);

      if (showToast) {
        toast({
          description: 'Seating plan refreshed successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      console.warn(error);
      // Check if session has expired/been deleted (500 error from booking info API)
      if (error?.response?.status === 500) {
        setShouldBlockNavigation(false);
        toast({
          title: 'Session Closed',
          description: 'Your booking session has been closed. Redirecting to booking page.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setTimeout(() => {
          if (isMountedRef.current) {
            window.location.href = '/#/admin/booking/create';
          }
        }, 2000);
        return;
      }
      if (showToast) {
        toast({
          description: 'Failed to refresh seating plan',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } finally {
      if (isUserRefresh && isMountedRef.current) {
        setIsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (isMounted) {
        await fetchData(false, false, false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Re-apply seat styling when any seat-related state changes
  useEffect(() => {
    if (svgRef.current) {
      applySeatStyling(svgRef.current, selectedSeat, lockByAdmin, seatWaiting, seatBooked);
    }
  }, [selectedSeat, lockByAdmin, seatWaiting, seatBooked]);
  const toast = useToast();
  const debouncedScrollAndUpdate = useCallback(
    debounce((seats, adultVal, childVal, pending) => {
      if (
        seats.length === parseInt(adultVal || 0) + parseInt(childVal || 0) &&
        seats.length !== 0 &&
        pending === 0
      ) {
        if (bookingRef.current) {
          // Use scrollIntoView without focusing — avoids Safari re-focusing the input
          bookingRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        setShowSubmitButton(true);
      } else {
        setShowSubmitButton(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedScrollAndUpdate(selectedSeat, adult, child, pendingSeatRequests);
    return () => {
      debouncedScrollAndUpdate.cancel();
    };
  }, [selectedSeat, adult, child, pendingSeatRequests]);

  // Always reads the latest adult/child from refs (not stale closures) so that
  // two updates fired close together (e.g. tabbing Adult -> Child quickly on a
  // slow machine/network) never send stale data. A sequence number discards
  // any response that resolves after a newer request was already sent, so a
  // slow first response can't overwrite a faster second one.
  const updateTicketCount = async (nextAdult, nextChild) => {
    const seq = ++ticketUpdateSeqRef.current;
    await bookingActions.updateBookingInfo({
      adult: parseInt(nextAdult || 0),
      child: parseInt(nextChild || 0),
    });
    const {
      data: { data: bookingData },
    } = await bookingActions.getBookingInfo();
    if (seq !== ticketUpdateSeqRef.current || !isMountedRef.current) return;

    setInfoSelectedSeat(bookingData);
    const seatsChoose =
      bookingData.seats?.map(
        (i) => `lune_seat_${i.seat_number}_${i.floor}_${i.zone?.id || i.zone_id}`
      ) || [];
    setSelectedSeat(seatsChoose);
  };

  function onSeatClick(event) {
    const seatNumber = event.target.getAttribute('id');
    console.log('Clicked seat:', seatNumber);
    console.log('listAssignSeatingPlan:', listAssignSeatingPlan);
    console.log('seatBooked:', seatBooked);
    
    const seat = listAssignSeatingPlan.find(
      (i) => `lune_seat_${i.seat}_${i.floor}_${i.zone_id}` === seatNumber
    );
    console.log('Found seat in listAssignSeatingPlan:', seat);
    
    if (seat) {
      window.open(
        `#/admin/booking/create/detail/${seat?.booking_id}`,
        '_blank'
      );
      return;
    }
    const [first, second, seat_number, floor, zone_id] = seatNumber.split('_');
    const totalSeat = parseInt(adult || 0) + parseInt(child || 0);
    if (totalSeat === 0) {
      bookingRef.current.scrollIntoView({ behavior: 'smooth' });
      toast({
        description: 'Please add number seat',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    const seatNumberBooked = seatBooked && seatBooked.map((i) => i.seat);
    if (seatNumberBooked.includes(seatNumber)) {
      toast({
        description: 'Not Allow',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    const filterSelectedSeat = selectedSeat.filter((i) => i !== seatNumber);
    if (filterSelectedSeat.length + 1 > totalSeat) {
      toast({
        description: "That's enough",
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (selectedSeat.includes(seatNumber)) {
      // Remove seat - verify via getBookingInfo response
      setPendingSeatRequests(prev => prev + 1);
      bookingActions
        .removeSeatBooking({
          seat_number,
          floor: parseInt(floor),
          zone_id: parseInt(zone_id),
        })
        .then((res) => {
          if (res.status === 200) {
            // Get booking info to verify seat was actually removed
            bookingActions.getBookingInfo().then((infoRes) => {
              if (infoRes.status === 200) {
                const bookingData = infoRes.data.data;
                setInfoSelectedSeat(bookingData);

                // Update selectedSeat based on actual seats in booking info
                const actualSeats = bookingData.seats?.map(
                  (i) => `lune_seat_${i.seat_number}_${i.floor}_${i.zone?.id || i.zone_id}`
                ) || [];
                setSelectedSeat(actualSeats);
              }
            });
          } else {
            toast({
              description: 'Failed to remove seat. Please try again.',
              status: 'error',
              duration: 5000,
              position: 'top-right',
            });
          }
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.meta?.message || 'Failed to remove seat. Please try again.';
          toast({
            description: errorMessage,
            status: 'error',
            duration: 5000,
            position: 'top-right',
          });
        })
        .finally(() => {
          setPendingSeatRequests(prev => prev - 1);
        });
    } else {
      // Add seat - verify via getBookingInfo response
      setPendingSeatRequests(prev => prev + 1);
      bookingActions
        .keepSeatBooking({
          seat_number,
          floor: parseInt(floor),
          zone_id: parseInt(zone_id),
        })
        .then((res) => {
          if (res.status === 200) {
            // Get booking info to verify seat was actually added
            bookingActions.getBookingInfo().then((infoRes) => {
              if (infoRes.status === 200) {
                const bookingData = infoRes.data.data;
                setInfoSelectedSeat(bookingData);

                // Update selectedSeat based on actual seats in booking info
                const actualSeats = bookingData.seats?.map(
                  (i) => `lune_seat_${i.seat_number}_${i.floor}_${i.zone?.id || i.zone_id}`
                ) || [];
                setSelectedSeat(actualSeats);
              }
            });
          } else {
            toast({
              description: 'Failed to keep seat. Please try again.',
              status: 'error',
              duration: 5000,
              position: 'top-right',
            });
          }
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.meta?.message || 'Failed to keep seat. Please try again.';
          toast({
            description: errorMessage,
            status: 'error',
            duration: 5000,
            position: 'top-right',
          });
        })
        .finally(() => {
          setPendingSeatRequests(prev => prev - 1);
        });
    }
  }

  const handleConfirmCancel = async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      await bookingActions.cancelBooking();
      resetCustomerDisplay();
      setShouldBlockNavigation(false);
      setIsCancelDialogOpen(false);
      setTimeout(() => {
        history.push('/admin/booking/create');
      }, 100);
    } catch (error) {
      console.error('Error canceling booking:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle navigation blocking with custom dialog
  const handleBlockedNavigation = useCallback((location) => {
    // Allow navigation within booking flow
    // Allow navigation to current page or payment page (within booking flow)
    if (location.pathname === '/admin/booking/create/information' ||
        location.pathname === '/admin/booking/create/payment') {
      return true;
    }

    // Store the pending navigation path and show dialog
    setPendingNavigationPath(location.pathname);
    setIsLeaveDialogOpen(true);
    return false;
  }, []);

  const handleConfirmLeave = async () => {
    setIsNavigating(true);
    try {
      await bookingActions.cancelBooking();
      resetCustomerDisplay();
      setShouldBlockNavigation(false);
      setIsLeaveDialogOpen(false);

      // Navigate to the pending path or default to booking create page
      const targetPath = pendingNavigationPath || '/admin/booking/create';
      setTimeout(() => {
        isProgrammaticNavigation.current = true;
        history.push(targetPath);
      }, 100);
    } catch (error) {
      console.error('Error canceling booking:', error);
      toast({
        description: 'Failed to cancel booking',
        status: 'error',
        duration: 3000,
        position: 'top-right',
      });
      setIsNavigating(false);
    }
  };

  const handleCancelLeave = () => {
    setIsLeaveDialogOpen(false);
    setPendingNavigationPath(null);
  };

  // Handle popstate (browser back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      // Only block if not programmatic navigation
      if (shouldBlockNavigation && !isProgrammaticNavigation.current) {
        // Prevent navigation by pushing current URL back
        window.history.pushState(null, '', '/#/admin/booking/create/information');
        // Set pending path to booking create page for back button
        setPendingNavigationPath('/admin/booking/create');
        setIsLeaveDialogOpen(true);
      }
      // Reset flag after handling
      isProgrammaticNavigation.current = false;
    };

    if (shouldBlockNavigation) {
      // Push current state to create history entry
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldBlockNavigation]);

  // Calendar modal functions
  const handleOpenCalendarModal = async () => {
    // Reset API call ref to allow fresh load
    calendarApiCallRef.current = null;
    setCalendarModalKey((k) => k + 1);

    // Set initial month/year and pre-load data with filters
    const currentDate = selectedShow?.booking?.show_date
      ? moment(selectedShow.booking.show_date)
      : moment();

    const year = currentDate.year();
    const month = currentDate.month();

    setYearUpdate(year);
    setMonthUpdate(month);

    // Pre-load calendar data with show/venue filter before opening modal
    const { firstDate, lastDate } = getFirstAndLastDate(year, month);
    const apiKey = `${month}-${year}`;
    calendarApiCallRef.current = apiKey;

    // Wait for API to complete before opening modal to prevent flash
    await bookingActions.getShowsSchedule({
      show_id: selectedShow?.booking?.show?.id,
      venue_id: selectedShow?.booking?.venue?.id,
      status: 'Active',
      from_date: firstDate,
      to_date: lastDate,
    });

    setIsCalendarModalOpen(true);
  };

  const handleCloseCalendarModal = () => {
    setIsCalendarModalOpen(false);
    calendarApiCallRef.current = null;
    // The 'calendar' broadcast effect above only fires while the modal is
    // open, so without this the Customer Display would be stuck showing the
    // calendar even after the operator closes it.
    broadcastSeatingScreen();
  };

  const getValueMonthChange = useCallback((month, year) => {
    const { firstDate, lastDate } = getFirstAndLastDate(year, month);
    const apiKey = `${month}-${year}`;

    // Prevent duplicate API calls for the same month/year
    if (calendarApiCallRef.current === apiKey) {
      return;
    }

    calendarApiCallRef.current = apiKey;
    setYearUpdate(year);
    setMonthUpdate(month);

    bookingActions.getShowsSchedule({
      show_id: selectedShow?.booking?.show?.id,
      venue_id: selectedShow?.booking?.venue?.id,
      status: 'Active',
      from_date: firstDate,
      to_date: lastDate,
    });
  }, [bookingActions, selectedShow]);

  const handleSelectShow = async (showScheduleDetailId, status) => {
    if (status === 3) return; // Don't allow selecting cancelled shows

    setIsChangingShow(true);
    try {
      // Cancel current booking session
      await bookingActions.cancelBooking();

      // Select new show
      const res = await bookingActions.selectShow({ id: parseInt(showScheduleDetailId) });

      if (res.status === 200) {
        const newSelectedShow = res.data.data;
        setSelectedShow(newSelectedShow);
        setIsCalendarModalOpen(false);

        // Reset all states
        setSelectedSeat([]);
        setAdult(0);
        setChild(0);
        adultRef.current = 0;
        childRef.current = 0;
        setInfoSelectedSeat({});
        setShowSubmitButton(false);

        // Reload all booking data with new session using window.location.reload
        // This ensures all components re-mount with the new session ID from recoil state
        setShouldBlockNavigation(false);

        toast({
          description: 'Show date changed successfully. Reloading...',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });

        // Wait a bit for state to persist, then reload
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error('Error changing show:', error);
      toast({
        description: 'Failed to change show. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      setIsChangingShow(false);
    }
  };

  // Handle beforeunload event (refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (shouldBlockNavigation) {
        e.preventDefault();
        e.returnValue = 'Are you sure you want to leave this page? Your current selection has not been completed. If you leave now, you will need to start this step again.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlockNavigation]);
  const bgProfile = useColorModeValue('white');
  const borderProfileColor = useColorModeValue('white', 'transparent');
  const uniqueStrings = [
    ...new Set((infoSelectedSeat?.seats || []).map((seat) => showTextFloor(seat.floor))),
  ];
  const concatenatedStrings = uniqueStrings.join(',');
  return (
    <>
      <Prompt
        when={shouldBlockNavigation}
        message={handleBlockedNavigation}
      />
      <Flex>
        <Flex
          w='100%'
          direction='column'
          mb='24px'
          justifyContent={{ sm: 'center', md: 'space-between' }}
          backdropFilter='blur(21px)'
          boxShadow='0px 2px 5.5px rgba(0, 0, 0, 0.02)'
          border='1.5px solid'
          borderColor={borderProfileColor}
          bg={bgProfile}
          p='24px'
        >
          <Text
            fontSize='var(--chakra-fontSizes-xl)'
            fontWeight='bold'
            ref={bookingRef}
          >
            Booking Information
          </Text>
          {selectedShow.booking && (
            <>
              <Flex
                my={2}
                sx={{
                  '@media (max-width: 479px)': {
                    flexDirection: 'column',
                    gap: 2,
                  },
                }}
              >
                <Box
                  w={{ base: '100%', sm: '50%' }}
                  sx={{
                    '@media (max-width: 479px)': {
                      w: '100%',
                    },
                  }}
                >
                  <Flex align='center'>
                    <Text
                      fontSize='var(--chakra-fontSizes-base)'
                      sx={{
                        minW: '65px',

                        '@media (max-width: 359px)': {
                          minW: '100px',
                        },
                      }}
                    >
                      Show Name:
                    </Text>{' '}
                    <Text
                      fontWeight='bold'
                      color={'green.400'}
                      fontSize={'var(--chakra-fontSizes-base)'}
                      ml={1}
                    >
                      {selectedShow.booking.show.name}
                    </Text>
                  </Flex>
                </Box>
                <Box
                  w={{ base: '100%', sm: '50%' }}
                  sx={{
                    '@media (max-width: 479px)': {
                      w: '100%',
                    },
                  }}
                >
                  <Flex align='center'>
                    <Text
                      fontSize='var(--chakra-fontSizes-base)'
                      sx={{
                        minW: '65px',

                        '@media (max-width: 359px)': {
                          minW: '100px',
                        },
                      }}
                    >
                      Venue:
                    </Text>{' '}
                    <Text
                      fontWeight='bold'
                      color={'green.400'}
                      fontSize={'var(--chakra-fontSizes-base)'}
                      ml={1}
                    >
                      {selectedShow.booking.venue.name}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
              <HSeparator />
              <Flex
                my={2}
                sx={{
                  '@media (max-width: 479px)': {
                    flexDirection: 'column',
                    gap: 2,
                  },
                }}
              >
                <Box
                  w={{ base: '100%', sm: '50%' }}
                  sx={{
                    '@media (max-width: 479px)': {
                      w: '100%',
                    },
                  }}
                >
                  <Flex align='center'>
                    <Text
                      fontSize='var(--chakra-fontSizes-base)'
                      sx={{
                        minW: '65px',

                        '@media (max-width: 359px)': {
                          minW: '100px',
                        },
                      }}
                    >
                      Show Date:
                    </Text>{' '}
                    <Text
                      fontWeight='bold'
                      color={'green.400'}
                      fontSize={'var(--chakra-fontSizes-base)'}
                      ml={1}
                    >
                      {moment(selectedShow.booking.show_date).format(
                        'DD/MM/YYYY'
                      )}
                    </Text>
                    <Tooltip label='Change show date' placement='top'>
                      <IconButton
                        aria-label='Change show date'
                        icon={<CalendarIcon />}
                        size='sm'
                        ml={3}
                        colorScheme='blue'
                        variant='ghost'
                        onClick={handleOpenCalendarModal}
                      />
                    </Tooltip>
                  </Flex>
                </Box>
                <Box
                  w={{ base: '100%', sm: '50%' }}
                  sx={{
                    '@media (max-width: 479px)': {
                      w: '100%',
                    },
                  }}
                >
                  <Flex align='center'>
                    <Text
                      fontSize='var(--chakra-fontSizes-base)'
                      sx={{
                        minW: '65px',

                        '@media (max-width: 359px)': {
                          minW: '100px',
                        },
                      }}
                    >
                      Show Time:
                    </Text>{' '}
                    <Text
                      fontWeight='bold'
                      color={'green.400'}
                      fontSize={'var(--chakra-fontSizes-base)'}
                      ml={1}
                    >
                      {selectedShow.booking.show_time.slice(0, -3)}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
              <HSeparator />
            </>
          )}
          <Flex
            gap={4}
            my={3}
            sx={{
              '@media (max-width: 359px)': {
                flexDirection: 'column',
                gap: 2,
              },
            }}
          >
            <Flex align='center'>
              <Text
                fontSize='var(--chakra-fontSizes-base)'
                mr={1}
              >
                Adult
              </Text>
              <NumericFormat
                customInput={SmallInput}
                thousandSeparator=','
                defaultValue={0}
                value={adult}
                onKeyDown={(e) => {
                  if (e.keyCode === 190) {
                    e.preventDefault();
                  }
                }}
                onChange={async (val) => {
                  const adultValue = val.target.value;
                  setAdult(adultValue || '');
                  adultRef.current = adultValue;
                  await updateTicketCount(adultRef.current, childRef.current);
                }}
              />
            </Flex>
            <Flex align='center'>
              <Text
                fontSize='var(--chakra-fontSizes-base)'
                mr={1}
              >
                Child (5-12 ages)
              </Text>
              <NumericFormat
                customInput={SmallInput}
                thousandSeparator=','
                defaultValue={0}
                value={child}
                onKeyDown={(e) => {
                  if (e.keyCode === 190) {
                    e.preventDefault();
                  }
                }}
                onChange={async (val) => {
                  const childValue = val.target.value;
                  setChild(childValue || '');
                  childRef.current = childValue;
                  await updateTicketCount(adultRef.current, childRef.current);
                }}
              />
            </Flex>
          </Flex>
          <HSeparator />

          <>
            <Flex
              my={2}
              sx={{
                '@media (max-width: 479px)': {
                  flexDirection: 'column',
                  gap: 2,
                },
              }}
            >
              <Box
                w={{ base: '100%', sm: '50%' }}
                sx={{
                  '@media (max-width: 479px)': {
                    w: '100%',
                  },
                }}
              >
                <Flex align='center'>
                  <Text
                    fontSize='var(--chakra-fontSizes-base)'
                    sx={{
                      minW: '65px',

                      '@media (max-width: 359px)': {
                        minW: '100px',
                      },
                    }}
                  >
                    Zone:
                  </Text>{' '}
                  <Text
                    fontWeight='bold'
                    color={'green.400'}
                    ml={1}
                    fontSize='var(--chakra-fontSizes-base)'
                  >
                    {infoSelectedSeat?.seats &&
                      infoSelectedSeat.seats[0]?.zone?.name}
                  </Text>
                </Flex>
              </Box>
              <Box
                w={{ base: '100%', sm: '50%' }}
                sx={{
                  '@media (max-width: 479px)': {
                    w: '100%',
                  },
                }}
              >
                <Flex align='center'>
                  <Text
                    fontSize='var(--chakra-fontSizes-base)'
                    sx={{
                      minW: '65px',

                      '@media (max-width: 359px)': {
                        minW: '100px',
                      },
                    }}
                  >
                    Floor:
                  </Text>{' '}
                  <Text
                    fontWeight='bold'
                    color={'green.400'}
                    ml={1}
                    fontSize='var(--chakra-fontSizes-base)'
                  >
                    <span>{concatenatedStrings}</span>
                  </Text>
                </Flex>
              </Box>
            </Flex>
            <HSeparator />
            <Flex
              my={2}
              sx={{
                '@media (max-width: 479px)': {
                  flexDirection: 'column',
                  gap: 2,
                },
              }}
            >
              <Box
                w={{ base: '100%', sm: '50%' }}
                sx={{
                  '@media (max-width: 479px)': {
                    w: '100%',
                  },
                }}
              >
                <Flex align='center'>
                  <Text
                    fontSize='var(--chakra-fontSizes-base)'
                    sx={{
                      minW: '65px',

                      '@media (max-width: 359px)': {
                        minW: '100px',
                      },
                    }}
                  >
                    Seat No.:
                  </Text>{' '}
                  <Text
                    cursor={'pointer'}
                    fontWeight='bold'
                    color={'green.400'}
                    ml={1}
                    fontSize='var(--chakra-fontSizes-base)'
                  >
                    {infoSelectedSeat?.seats
                      ?.map((seat) => seat.seat_number)
                      .join(', ')}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          </>
          <Flex align='center' justify='center' wrap='wrap' rowGap={2} gap={4}>
            <Button
              colorScheme='blue'
              disabled={!showSubmitButton}
              onClick={() => {
                broadcastBookingConfirmation();
                isProgrammaticNavigation.current = true;
                history.push('/admin/booking/create/payment');
              }}
            >
              Submit
            </Button>
            <Button
              onClick={() => setIsCancelDialogOpen(true)}
              isLoading={isCancelling}
              disabled={isCancelling}
            >
              Cancel
            </Button>
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme='teal'
              onClick={() => fetchData(true, true, true)}
              isLoading={isRefreshing}
              disabled={isRefreshing}
            >
              Refresh
            </Button>
            {checkForbidden(179) && (
              <Button
                variant='outline'
                disabled={isDisplayConnected}
                onClick={openCustomerDisplayWindow}
              >
                {isDisplayConnected ? 'Customer Display Opened' : 'Open Customer Display'}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Box bg='white'>
        <Flex
          position='relative'
          sx={{
            '@media (max-width: 359px)': {
              paddingTop: '16px',
              flexDirection: 'column',
            },
          }}
        >
          <Stack
            position='absolute'
            p={'5px 10px'}
            // backgroundColor={"white"}
            left='0'
            direction='column'
            sx={{
              '@media (max-width: 359px)': {
                position: 'static',
                width: '100%',
              },
            }}
          >
            <Text
              fontWeight={'bold'}
              color='#939496'
              textAlign='center'
              fontSize='var(--chakra-fontSizes-base)'
            >
              LEGEND IN ZONES
            </Text>
            <Grid
              templateColumns='repeat(3, auto)'
              gap='6px'
              fontSize={{ base: '8px', md: 'var(--chakra-fontSizes-base)' }}
              color='white'
              textAlign='center'
            >
              <Text color='#939496'>ZONE</Text>
              <Text color='#939496'>SEATS</Text>
              <Text color='#939496'>OFFICIAL RATE (VND)</Text>
              {ticketPrices.map((i, index) => (
                <React.Fragment key={index}>
                  <Text
                    textAlign='center'
                    backgroundColor={colorBackGround(i.zone_id)}
                    px={1}
                    fontSize={{ base: '8px', md: 'var(--chakra-fontSizes-base)' }}
                    fontWeight={'bold'}
                    py={2}
                  >
                    {i.zone.name}
                  </Text>
                  <Text
                    textAlign='center'
                    backgroundColor={colorBackGround(i.zone_id)}
                    px={3}
                    fontSize={{ base: '8px', md: 'var(--chakra-fontSizes-base)' }}
                    fontWeight={'bold'}
                    py={2}
                  >
                    {i?.total_seat}
                  </Text>
                  <Text
                    px={3}
                    fontSize={{ base: '8px', md: 'var(--chakra-fontSizes-base)' }}
                    fontWeight={'bold'}
                    py={2}
                    textAlign='center'
                    backgroundColor={colorBackGround(i.zone_id)}
                  >
                    {i.official_price.toLocaleString('en-US')}
                  </Text>
                </React.Fragment>
              ))}
            </Grid>
          </Stack>
          <Flex
            position='absolute'
            right='10px'
            backgroundColor='white'
            padding='10px'
            width={{ xs: 'fit-content' }}
            sx={{
              '@media (max-width: 359px)': {
                position: 'static',
                width: '100%',

                '& img': {
                  maxWidth: '100%',
                },
              },
            }}
          >
            {legendMap && (
              <Image maxW={{ base: '140px', md: '250px' }} src={legendMap} />
            )}
          </Flex>
        </Flex>
        <ReactSVG
          id='svgImage'
          src={desktopMap}
          afterInjection={(svg) => {
            // Store SVG reference
            svgRef.current = svg;

            // Add click event listeners
            const seats = svg.querySelectorAll('circle');
            seats.forEach((seat) => {
              seat.addEventListener('click', onSeatClick);
            });

            // Apply initial seat styling
            applySeatStyling(svg, selectedSeat, lockByAdmin, seatWaiting, seatBooked);
          }}
        />
      </Box>
      <AlertDialog
        isOpen={isCancelDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsCancelDialogOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg='white' borderRadius='12px' px={4} py={4} maxW='420px' w='90%'>
            <IconButton
              aria-label='Close dialog'
              icon={<CloseIcon />}
              position='absolute'
              top={3}
              right={3}
              onClick={() => setIsCancelDialogOpen(false)}
              borderRadius='full'
              border='2px solid'
              borderColor='blue.100'
              bg='white'
              _hover={{ bg: 'gray.50' }}
              size='sm'
            />

            <AlertDialogHeader fontSize='lg' fontWeight='700' color='#0DB34A' textAlign='left'>
              Cancel Booking
            </AlertDialogHeader>

            <AlertDialogBody pt={2} pb={4} color='gray.700' fontSize='sm' textAlign='left'>
              Are you sure you want to cancel this booking?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setIsCancelDialogOpen(false)}
                bg='gray.100'
                _hover={{ bg: 'gray.200' }}
                color='gray.800'
                borderRadius='10px'
                px={4}
                py={2}
                size='sm'
              >
                No
              </Button>
              <Button
                bg='#053957'
                color='white'
                ml={3}
                onClick={handleConfirmCancel}
                isLoading={isCancelling}
                _hover={{ bg: '#042e45' }}
                borderRadius='10px'
                px={4}
                py={2}
                size='sm'
              >
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        isOpen={isLeaveDialogOpen}
        leastDestructiveRef={leaveRef}
        onClose={handleCancelLeave}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg='white' borderRadius='12px' px={4} py={4} maxW='420px' w='90%'>
            <IconButton
              aria-label='Close dialog'
              icon={<CloseIcon />}
              position='absolute'
              top={3}
              right={3}
              onClick={handleCancelLeave}
              borderRadius='full'
              border='2px solid'
              borderColor='blue.100'
              bg='white'
              _hover={{ bg: 'gray.50' }}
              size='sm'
            />

            <AlertDialogHeader fontSize='lg' fontWeight='700' color='#0DB34A' textAlign='left'>
              Leave Booking Page
            </AlertDialogHeader>

            <AlertDialogBody pt={2} pb={4} color='gray.700' fontSize='sm' textAlign='left'>
              Are you sure you want to leave this page? Your current selection has not been completed. If you leave now, you will need to start this step again.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={leaveRef}
                onClick={handleCancelLeave}
                bg='gray.100'
                _hover={{ bg: 'gray.200' }}
                color='gray.800'
                borderRadius='10px'
                px={4}
                py={2}
                size='sm'
              >
                Stay On Page
              </Button>
              <Button
                bg='#053957'
                color='white'
                ml={3}
                onClick={handleConfirmLeave}
                isLoading={isNavigating}
                _hover={{ bg: '#042e45' }}
                borderRadius='10px'
                px={4}
                py={2}
                size='sm'
              >
                Leave & Cancel Booking
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {selectedShow?.booking && (
        <Portal>
          <Box>
            <CountDown
              expTime={selectedShow.booking.expire_at}
              onExpire={() => {
                setShouldBlockNavigation(false);
                resetCustomerDisplay();
              }}
            />
          </Box>
        </Portal>
      )}
      <Modal
        isOpen={isCalendarModalOpen}
        onClose={handleCloseCalendarModal}
        size='6xl'
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxW='90vw' maxH='90vh'>
          <ModalHeader>
            Change Show Date
            <Text fontSize='sm' fontWeight='normal' color='gray.600' mt={1}>
              Current: {selectedShow?.booking?.show?.name} - {selectedShow?.booking?.show_date ? moment(selectedShow.booking.show_date).format('DD/MM/YYYY') : ''} - {selectedShow?.booking?.show_time?.slice(0, -3)}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY='auto'>
            <Box bg='#ebebeb' p={4} borderRadius='md'>
              <EventCalendar
                key={calendarModalKey}
                calendarData={showsSchedule.filter((show) => {
                  // Only show events from today onwards
                  const showDate = moment(show.start);
                  const today = moment().startOf('day');
                  return showDate.isSameOrAfter(today);
                })}
                initialDate={moment().month(monthUpdate).year(yearUpdate).startOf('month').format('YYYY-MM-DD')}
                yearUpdate={yearUpdate}
                monthUpdate={monthUpdate}
                getValueMonthChange={(month, year) => getValueMonthChange(month, year)}
                selectShow={(showId, status) => handleSelectShow(showId, status)}
              />
            </Box>
            {isChangingShow && (
              <Flex justify='center' align='center' p={4}>
                <Text>Changing show date, please wait...</Text>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <CustomerDisplayPreview />
    </>
  );
}

export default BookingInformation;
